import { Clipboard } from '@angular/cdk/clipboard'
import { CurrencyPipe, NgClass } from '@angular/common'
import { HttpErrorResponse } from '@angular/common/http'
import { Component, computed, DestroyRef, inject, OnInit, signal, viewChild } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { WorkspaceRoleService } from '@app/modules/auth/services/workspace-role.service'
import { BillingService, roundMoney } from '@app/modules/payments/services/billing.service'
import { Alert } from '@app/v2/shared/enums/alert.enum'
import { SafeDomPipe } from '@app/v2/shared/pipes/safe-dom.pipe'
import { AlertService } from '@app/v2/shared/services/alert.service'
import { ModalService } from '@app/v2/shared/services/modal.service'
import { TranslateModule, TranslateService } from '@ngx-translate/core'
import { format, parse } from 'date-fns'
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader'
import { NgxTippyModule } from 'ngx-tippy-wrapper'
import { forkJoin, of, Subject } from 'rxjs'
import { catchError, finalize, map, switchMap } from 'rxjs/operators'
import { BillingIcons } from '../../../models/organization-settings.model'
import { OrganizationService } from '../../../services/organization.service'
import { OrganizationBilling } from '../../../types/organization-settings.type'
import { AddPaymentCardModalComponent } from './add-payment-card-modal/add-payment-card-modal.component'
import { BillingTransactionsComponent } from './billing-transactions/billing-transactions.component'
import { formatCardBrand, formatCardExpiry, getCardBrandIconUrl } from './card-brand-icon'
import { BillingPaymentMethod, PaymentCardsModalComponent } from './payment-cards-modal/payment-cards-modal.component'
import { WalletTopUpModalComponent } from './wallet-top-up-modal/wallet-top-up-modal.component'

@Component({
  selector: 'app-billing',
  imports: [
    TranslateModule,
    SafeDomPipe,
    NgxTippyModule,
    NgxSkeletonLoaderModule,
    CurrencyPipe,
    NgClass,
    PaymentCardsModalComponent,
    AddPaymentCardModalComponent,
    WalletTopUpModalComponent,
    BillingTransactionsComponent
  ],
  templateUrl: './billing.component.html'
})
export class BillingComponent implements OnInit {
  public icons = BillingIcons.getIconsConfiguration()
  public isLoading = signal(true)
  public billing = signal<OrganizationBilling | null>(null)
  public paymentMethods = signal<BillingPaymentMethod[]>([])
  /** False when the billing API returns 403 (no permission to view billing). */
  private readonly _hasBillingAccess = signal(true)
  /** Stripe payment method ID currently being deleted, if any. */
  public removingPaymentMethodId = signal<string | null>(null)
  public readonly paymentCardsModalId = 'billing-payment-cards-modal'
  public readonly addPaymentCardModalId = 'billing-add-payment-card-modal'
  public readonly walletTopUpModalId = 'billing-wallet-top-up-modal'
  public readonly emptyStateImage = 'assets/images/v2/noTransactions.svg'

  public readonly accessDeniedImage = 'assets/images/v2/noBillingAccess.png'
  public customerIdCopied = signal(false)

  /**
   * Tooltip text for the Stripe customer ID copy button.
   */
  public customerIdTippyContent = computed(() =>
    this._translateService.instant(this.customerIdCopied() ? 'v2.orgSettings.billing.copiedId' : 'v2.orgSettings.billing.clickToCopyId')
  )

  private readonly _billingService = inject(BillingService)
  private readonly _alertService = inject(AlertService)
  private readonly _clipboard = inject(Clipboard)
  private readonly _translateService = inject(TranslateService)
  private readonly _organizationService = inject(OrganizationService)
  private readonly _workspaceRole = inject(WorkspaceRoleService)
  private readonly _modalService = inject(ModalService)
  private readonly _destroyRef = inject(DestroyRef)
  private readonly _reloadBillingDetails$ = new Subject<{ silent: boolean }>()
  private _customerIdCopyResetTimer: ReturnType<typeof setTimeout> | null = null
  /** Optimistic wallet balance after a top-up until the billing API catches up. */
  private _topUpExpectedBalance: number | null = null

  private readonly _billingTransactions = viewChild(BillingTransactionsComponent)

  /** Stable card formatter refs for child modal inputs (avoids `.bind(this)` in the template). */
  public readonly getCardBrandIconUrlFn = getCardBrandIconUrl
  public readonly formatCardExpiryFn = formatCardExpiry
  public readonly formatCardBrandFn = formatCardBrand

  /**
   *
   * Stripe customer ID for the billing workspace.
   */
  public customerId = computed(() => this.billing()?.stripe_customer_id ?? '')

  /**
   * Wallet balance from the organization billing payload.
   */
  public walletBalance = computed(() => this.billing()?.wallet_balance ?? 0)

  /**
   * Default saved payment method for the wallet card footer.
   */
  public defaultPaymentMethod = computed(() => {
    const methods = this.paymentMethods()
    if (!methods.length) return null

    return methods.find((method) => method.is_default) ?? methods[0]
  })

  /**
   * Whether the workspace has any saved payment methods.
   */
  public hasPaymentMethods = computed(() => this.paymentMethods().length > 0)

  /**
   * Whether the workspace is on the basic plan.
   */
  public isBasicPlan = computed(() => {
    const subscription = this.billing()?.subscription
    if (!subscription) return true

    return subscription.plan_name.toLowerCase() === 'basic' || subscription.price === 0
  })

  /**
   * Whether the current workspace is on a paid (non-basic) plan, derived from billing API data.
   */
  public isProPlan = computed(() => {
    const subscription = this.billing()?.subscription
    if (!subscription) return false

    return !this.isBasicPlan()
  })

  /**
   * Display label for the current plan name.
   */
  public planName = computed(() => this.billing()?.subscription?.plan_name ?? 'Basic')

  /**
   * Staff-toggled PRO. Backend sends placeholder price `0.85` instead of a real Stripe amount.
   */
  public isManuallyUpgraded = computed(() => {
    const price = this.billing()?.subscription?.price
    return Number(price) === 0.85
  })

  /**
   * Main plan price amount (e.g. "$0", "$499"). Manual upgrades show a label instead of the placeholder price.
   */
  public planPriceAmount = computed(() => {
    if (this.isBasicPlan()) return '$0'

    if (this.isManuallyUpgraded()) {
      return this._translateService.instant('v2.orgSettings.billing.manuallyUpgraded')
    }

    const subscription = this.billing()?.subscription
    if (!subscription) return ''

    const currencySymbol = subscription.currency?.toLowerCase() === 'usd' ? '$' : `${subscription.currency?.toUpperCase()} `
    return `${currencySymbol}${subscription.price}`
  })

  /**
   * Plan price suffix (e.g. "/ forever", "/ month").
   */
  public planPriceSuffix = computed(() => {
    const forever = ` / ${this._translateService.instant('v2.orgSettings.billing.forever')}`

    if (this.isBasicPlan()) return forever

    const subscription = this.billing()?.subscription
    if (!subscription) return ''

    if (this.isManuallyUpgraded()) {
      return subscription.expires_at ? '' : forever
    }

    if (!subscription.expires_at) return forever

    return subscription.billing_frequency ? ` / ${subscription.billing_frequency}` : ''
  })

  /**
   * Formatted plan expiry date for the current plan card (only when a date exists).
   */
  public planExpiryDate = computed(() => {
    if (this.isBasicPlan()) return null

    const expiresAt = this.billing()?.subscription?.expires_at
    if (!expiresAt) return null

    return format(parse(expiresAt, 'yyyy-MM-dd HH:mm:ss', new Date()), 'dd MMM yyyy')
  })

  ngOnInit(): void {
    this._reloadBillingDetails$
      .pipe(
        switchMap(({ silent }) => {
          if (!silent) {
            this.isLoading.set(true)
          }

          return forkJoin({
            billing: this._organizationService.getOrganizationBilling(this._billingService.workspaceId).pipe(
              map((data) => ({ data, forbidden: false as const })),
              catchError((error: HttpErrorResponse) => of({ data: null, forbidden: error?.status === 403 }))
            ),
            paymentMethods: this._organizationService.getOrganizationPaymentMethods(this._billingService.workspaceId).pipe(catchError(() => of([])))
          }).pipe(
            finalize(() => {
              if (!silent) {
                this.isLoading.set(false)
              }
            })
          )
        }),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe(({ billing, paymentMethods }) => {
        if (billing.forbidden) {
          this._hasBillingAccess.set(false)
          this.billing.set(null)
          this.paymentMethods.set([])
          return
        }

        this._hasBillingAccess.set(true)
        this._applyBillingDetails(billing.data, paymentMethods)
      })

    this._billingService.reloadBillingDetails$.pipe(takeUntilDestroyed(this._destroyRef)).subscribe(() => {
      this._reloadBillingDetails$.next({ silent: true })
      this._refreshTransactions()
    })

    this._destroyRef.onDestroy(() => {
      if (this._customerIdCopyResetTimer) {
        clearTimeout(this._customerIdCopyResetTimer)
      }
    })

    this._reloadBillingDetails$.next({ silent: false })
  }

  /**
   * Refresh the transactions list after a billing change (e.g. wallet top-up).
   */
  private _refreshTransactions(): void {
    this._billingTransactions()?.refresh(0)
  }

  /**
   * Apply an optimistic wallet balance bump after a successful top-up and broadcast it.
   * @param {number} amount Top-up amount in USD
   */
  public onWalletTopUpSuccess(amount: number): void {
    this.billing.update((current) => {
      if (!current) return current

      const nextBalance = roundMoney(Number(current.wallet_balance ?? 0) + amount)
      this._topUpExpectedBalance = nextBalance
      this._billingService.publishWalletBalance(nextBalance)

      return {
        ...current,
        wallet_balance: nextBalance
      }
    })
  }

  /**
   * Update billing and payment method state from a refetch response.
   * @param {OrganizationBilling | null} billing Organization billing payload
   * @param {BillingPaymentMethod[]} paymentMethods Saved payment methods
   */
  private _applyBillingDetails(billing: OrganizationBilling | null, paymentMethods: BillingPaymentMethod[]): void {
    if (billing) {
      if (this._shouldKeepOptimisticTopUpBalance(billing)) {
        const expectedBalance = this._topUpExpectedBalance!
        this.billing.update((current) => {
          if (!current) return current

          return {
            ...current,
            wallet_balance: expectedBalance
          }
        })
        this._billingService.publishWalletBalance(expectedBalance)
        this.paymentMethods.set(paymentMethods)
        return
      }

      const hadPendingTopUpSync = this._topUpExpectedBalance !== null
      this._topUpExpectedBalance = null
      this.billing.set(billing)
      this._billingService.publishWalletBalance(Number(billing.wallet_balance ?? 0))

      if (hadPendingTopUpSync) {
        this._refreshTransactions()
      }
    }

    this.paymentMethods.set(paymentMethods)
  }

  /**
   * Whether the API wallet balance still lags the optimistic top-up value.
   * @param {OrganizationBilling} billing Organization billing payload from the API
   * @returns {boolean} True when the optimistic balance should keep being shown
   */
  private _shouldKeepOptimisticTopUpBalance(billing: OrganizationBilling): boolean {
    if (this._topUpExpectedBalance === null) return false

    const apiBalance = roundMoney(Number(billing.wallet_balance ?? 0))
    return apiBalance < this._topUpExpectedBalance
  }

  /**
   * Copy the Stripe customer ID to the clipboard and show a copied tooltip.
   */
  public copyCustomerId(): void {
    const id = this.customerId()
    if (!id) return

    this._clipboard.copy(id)
    this.customerIdCopied.set(true)

    if (this._customerIdCopyResetTimer) {
      clearTimeout(this._customerIdCopyResetTimer)
    }

    this._customerIdCopyResetTimer = setTimeout(() => {
      this.customerIdCopied.set(false)
      this._customerIdCopyResetTimer = null
    }, 2000)
  }

  /**
   * Open the payment cards modal (lists all saved cards).
   */
  public openPaymentCardsModal(): void {
    this._modalService.toggleModal(this.paymentCardsModalId)
  }

  /**
   * Open the add payment card modal directly.
   */
  public openAddPaymentCardModal(): void {
    this._modalService.toggleModal(this.addPaymentCardModalId)
  }

  /**
   * Open the wallet top-up modal.
   */
  public openWalletTopUpModal(): void {
    this._modalService.toggleModal(this.walletTopUpModalId)
  }

  /**
   * Set a payment method as the organization default.
   */
  public setDefaultPaymentMethod(method: BillingPaymentMethod): void {
    this._organizationService
      .setDefaultPaymentMethod(this._billingService.workspaceId, method.id)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(() => {
        this._updateLocalDefaultPaymentMethod(method.id)
        this._refetchPaymentMethods(method.id)
      })
  }

  /**
   * Delete a saved payment method from the organization.
   */
  public removePaymentMethod(method: BillingPaymentMethod): void {
    this.removingPaymentMethodId.set(method.id)

    this._organizationService
      .deleteOrganizationPaymentMethod(this._billingService.workspaceId, method.id)
      .pipe(
        finalize(() => this.removingPaymentMethodId.set(null)),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe({
        next: () => {
          this._removeLocalPaymentMethod(method.id)
          this._refetchPaymentMethods()

          if (!this.paymentMethods().length) {
            this._closePaymentCardsModal()
          }
        },
        error: () => {
          this._alertService.alert(Alert.ERROR, this._translateService.instant('v2.orgSettings.billing.removePaymentCardError'))
        }
      })
  }

  /**
   * Close the payment cards listing modal when it is open.
   */
  private _closePaymentCardsModal(): void {
    if (this._modalService.isModalOpen(this.paymentCardsModalId)) {
      this._modalService.toggleModal(this.paymentCardsModalId)
    }
  }

  /**
   * Update the local default flag immediately so the UI reflects the change before refetch.
   * @param {string} paymentMethodId Stripe payment method ID to mark as default
   */
  private _updateLocalDefaultPaymentMethod(paymentMethodId: string): void {
    this.paymentMethods.update((methods) =>
      methods.map((item) => ({
        ...item,
        is_default: item.id === paymentMethodId
      }))
    )
  }

  /**
   * Remove a payment method from local state immediately so the UI reflects the change before refetch.
   * @param {string} paymentMethodId Stripe payment method ID to remove
   */
  private _removeLocalPaymentMethod(paymentMethodId: string): void {
    this.paymentMethods.update((methods) => methods.filter((item) => item.id !== paymentMethodId))
  }

  /**
   * Refetch payment methods without toggling the page skeleton.
   * @param {string} [mergeDefaultPaymentMethodId] Optional default card ID to preserve when the API is stale
   */
  private _refetchPaymentMethods(mergeDefaultPaymentMethodId?: string): void {
    this._organizationService
      .getOrganizationPaymentMethods(this._billingService.workspaceId)
      .pipe(
        catchError(() => of([])),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe((methods) => {
        const nextMethods = mergeDefaultPaymentMethodId ? this._mergeDefaultPaymentMethod(methods, mergeDefaultPaymentMethodId) : methods

        this.paymentMethods.set(nextMethods)
      })
  }

  /**
   * Prefer the API default flag when it matches; otherwise keep the locally known default.
   * @param {BillingPaymentMethod[]} methods Payment methods from the API
   * @param {string} defaultPaymentMethodId Payment method ID that was just set as default
   * @returns {BillingPaymentMethod[]} Payment methods with a single default card
   */
  private _mergeDefaultPaymentMethod(methods: BillingPaymentMethod[], defaultPaymentMethodId: string): BillingPaymentMethod[] {
    const apiDefault = methods.find((method) => method.is_default)
    if (apiDefault?.id === defaultPaymentMethodId) {
      return methods
    }

    return methods.map((method) => ({
      ...method,
      is_default: method.id === defaultPaymentMethodId
    }))
  }

  /**
   * Whether the current user can edit billing (add/remove cards, manage plan).
   */
  public canEditBilling(): boolean {
    return this._workspaceRole.canEditBilling()
  }

  /**
   * Request a customer portal session and open it in a new browser tab.
   */
  public openCustomerPortal(): void {
    this._organizationService
      .createOrganizationCustomerPortal(this._billingService.workspaceId, this._getCustomerPortalReturnUrl())
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((result) => {
        window.open(result.url, '_blank')
      })
  }

  /**
   * Build the return URL that brings the user back to billing settings after Stripe portal.
   * Preserves workspace_id query params so the org-settings deep link still restores currentWorkspace.
   * @returns {string} Absolute URL with billing section fragment
   */
  private _getCustomerPortalReturnUrl(): string {
    const { origin, pathname, search } = window.location
    return `${origin}${pathname}${search}#billing`
  }

  /**
   * Gets skeleton theme colors based on current theme.
   */
  public getSkeletonTheme(): { backgroundColor: string; color: string } {
    return this._organizationService.getSkeletonTheme()
  }

  /**
   * Whether the current user can view billing details.
   * False when the billing API returned 403 (show the no-access empty state).
   * @returns {boolean} True when billing content should be shown
   */
  public hasBillingAccess(): boolean {
    return this._hasBillingAccess()
  }
}
